import { GetIn } from "@/features/planner/api/planner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface GetInGuideProps {
    data?: GetIn;
}

export const GetInGuide = ({ data }: GetInGuideProps) => {
    if (!data) return null;

    return (
        <div className="mx-5 p-10 bg-white">
            <h2 className="text-center text-[28px] font-bold text-[#187A85] mb-6 font-serif">
                How to Get In
            </h2>

            {/* Arrival Guide */}
            <div className="bg-[#FFF9F5] rounded-xl p-6 mb-4">
                <div className="text-[14px] text-[#333333] leading-relaxed markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {data.arrival_guide || "Information not available."}
                    </ReactMarkdown>
                </div>
            </div>

            {/* Quick Facts */}
            {data.quick_facts && Object.keys(data.quick_facts).length > 0 && (
                <div className="bg-[#FFF9F5] rounded-xl p-6 mb-4">
                    <h4 className="text-[16px] font-bold text-[#E76828] mb-3">
                        Quick Facts
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(data.quick_facts).map(([key, value]) => (
                            <div key={key} className="flex flex-col">
                                <span className="text-[12px] text-[#666666] uppercase tracking-wide">
                                    {key.replace(/_/g, ' ')}
                                </span>
                                <span className="text-[14px] text-[#333333] font-medium">
                                    {value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Step by Step Summary */}
            {data.step_by_step_summary && data.step_by_step_summary.length > 0 && (
                <div className="bg-[#FFF9F5] rounded-xl p-6 mb-4">
                    <h4 className="text-[16px] font-bold text-[#E76828] mb-3">
                        Step-by-Step Guide
                    </h4>
                    <ol className="list-decimal list-inside space-y-2">
                        {data.step_by_step_summary.map((step, index) => (
                            <li key={index} className="text-[14px] text-[#333333] leading-relaxed">
                                {step}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Insider Tips */}
                {data.insider_tips && data.insider_tips.length > 0 && (
                    <SectionCard
                        title="💡 Insider Tips"
                        items={data.insider_tips}
                    />
                )}

                {/* Money Saving Tips */}
                {data.money_saving_tips && data.money_saving_tips.length > 0 && (
                    <SectionCard
                        title="💰 Money Saving Tips"
                        items={data.money_saving_tips}
                    />
                )}
            </div>

            {/* Common Mistakes */}
            {data.common_mistakes && data.common_mistakes.length > 0 && (
                <div className="bg-[#FFF5F5] rounded-xl p-6 mb-4 border border-[#FFE0E0]">
                    <h4 className="text-[16px] font-bold text-[#D32F2F] mb-3">
                        ⚠️ Common Mistakes to Avoid
                    </h4>
                    <ul className="space-y-2">
                        {data.common_mistakes.map((mistake, index) => (
                            <li key={index} className="text-[14px] text-[#333333] leading-relaxed flex items-start gap-2">
                                <span className="text-[#D32F2F]">•</span>
                                {mistake}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Disclaimer */}
            {data.disclaimer && (
                <div className="bg-[#F5F5F5] rounded-xl p-4 mb-4">
                    <p className="text-[12px] text-[#666666] italic">
                        {data.disclaimer}
                    </p>
                    {data.verified_date && (
                        <p className="text-[11px] text-[#999999] mt-1">
                            Verified: {data.verified_date}
                        </p>
                    )}
                </div>
            )}

            {/* Source URLs */}
            {data.source_urls && data.source_urls.length > 0 && (
                <div className="text-[12px] text-[#666666]">
                    <span className="font-medium">Sources: </span>
                    {data.source_urls.map((url, index) => (
                        <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#187A85] hover:underline mr-2"
                        >
                            [{index + 1}]
                        </a>
                    ))}
                </div>
            )}

            <style>{`
                .markdown-content p { margin-bottom: 0.5em; }
                .markdown-content p:last-child { margin-bottom: 0; }
                .markdown-content strong { color: #000; font-weight: 700; }
                .markdown-content a { color: #187A85; text-decoration: underline; }
            `}</style>
        </div>
    );
};

const SectionCard = ({ title, items }: { title: string, items: string[] }) => (
    <div className="bg-[#FFF9F5] rounded-xl p-6 h-full">
        <h4 className="text-[16px] font-bold text-[#E76828] mb-3">
            {title}
        </h4>
        <ul className="space-y-2">
            {items.map((item, index) => (
                <li key={index} className="text-[14px] text-[#333333] leading-relaxed flex items-start gap-2">
                    <span className="text-[#E76828]">•</span>
                    {item}
                </li>
            ))}
        </ul>
    </div>
);
